/**
 * Zod schemas for the Agentic Search API (C14).
 *
 * ``POST /search/agent`` runs an LLM-driven tool-using loop over the corpus
 * (keyword_search, semantic_search, fetch_document_text, cite) and streams
 * partial output to the client via Server-Sent Events (SSE).
 *
 * This module defines the canonical shapes shared by the API route, the
 * agent service, and any client (frontend, saved-search runner):
 *
 *  - the request body (``AgentSearchRequest``),
 *  - the SSE event envelope (``AgentEvent``) and each event variant,
 *  - the validated citation (``AgentCitation``),
 *  - the terminal aggregate result (``AgentSearchResult``) — the same data
 *    the ``end`` event carries, broken out for non-streaming consumers.
 *
 * The SSE wire format is: each event is emitted as a line
 * ``event: <type>\n`` followed by ``data: <json>\n\n`` where ``<json>`` is
 * the JSON encoding of the matching ``AgentEvent`` object (which always
 * includes its own ``type`` discriminator so the payload is self-describing
 * even if a proxy strips the SSE ``event:`` line).
 */
import { z } from "zod";
import { SearchFiltersSchema } from "./schemas/search.js";

// ── Request ───────────────────────────────────────────────────────────────────

/**
 * POST /search/agent request body.
 *
 * ``q`` is a natural-language question. ``filters`` (optional) constrain the
 * corpus the agent's search tools may draw from, exactly as in C13. The
 * server caps the tool-call budget, wall clock, and cost regardless of any
 * client hint, but a client MAY request a *lower* ``maxToolCalls`` for a
 * cheaper run; the effective value is ``min(client, server cap)``.
 */
export const AgentSearchRequestSchema = z
  .object({
    q: z.string().min(1).max(2000),
    filters: SearchFiltersSchema.optional(),
    /**
     * Optional client-requested upper bound on tool calls. The server
     * still enforces its own hard cap; this can only make a run cheaper.
     */
    maxToolCalls: z.number().int().min(1).max(50).optional(),
  })
  .strict();

export type AgentSearchRequest = z.infer<typeof AgentSearchRequestSchema>;

// ── Citation ────────────────────────────────────────────────────────────────

/**
 * A validated citation. Every ``[n]`` marker in the agent's answer maps to
 * one of these. The server validates each citation before emitting it:
 *
 *  - ``chunkId`` must reference an existing ``document_chunk`` row.
 *  - ``sourceUrl`` (when present) must belong to an indexed ``source`` — the
 *    agent cannot fabricate a URL or cite arbitrary external pages.
 */
export const AgentCitationSchema = z.object({
  /** 1-based marker index as it appears in the answer text (``[n]``). */
  marker: z.number().int().min(1),
  documentId: z.string().uuid(),
  chunkId: z.string().uuid(),
  jurisdictionId: z.string().uuid().nullable(),
  jurisdictionName: z.string().nullable(),
  documentTitle: z.string().nullable(),
  /** A source URL that belongs to an indexed ``source`` row, or null. */
  sourceUrl: z.string().url().nullable(),
  /** Short supporting quote/snippet pulled from the cited chunk. */
  snippet: z.string(),
});

export type AgentCitation = z.infer<typeof AgentCitationSchema>;

// ── Tool call telemetry ───────────────────────────────────────────────────────

/**
 * The set of tools the agent is permitted to call. There is intentionally
 * NO ``http_get`` / arbitrary-URL tool: the agent can only touch the indexed
 * corpus. An attempt to call anything outside this set is logged and ignored.
 */
export const AgentToolNameEnum = z.enum([
  "keyword_search",
  "semantic_search",
  "fetch_document_text",
  "cite",
]);

export type AgentToolName = z.infer<typeof AgentToolNameEnum>;

/**
 * Why an agent run stopped. ``completed`` is the happy path; the others are
 * graceful early stops that still return a (partial) answer + cites.
 */
export const AgentStopReasonEnum = z.enum([
  "completed",
  "tool_budget_exceeded",
  "wall_clock_exceeded",
  "cost_cap_exceeded",
]);

export type AgentStopReason = z.infer<typeof AgentStopReasonEnum>;

// ── SSE events ────────────────────────────────────────────────────────────────

/**
 * Emitted once, first, when the run starts.
 */
export const AgentStartEventSchema = z.object({
  type: z.literal("start"),
  /** Server-assigned id correlating the run across logs/traces. */
  runId: z.string().min(1),
  /** Versioned prompt id used for this run (A/B switch). */
  promptVersion: z.string().min(1),
  /** Effective per-request budgets after clamping client hints. */
  toolCallBudget: z.number().int().positive(),
  wallClockMs: z.number().int().positive(),
});

export type AgentStartEvent = z.infer<typeof AgentStartEventSchema>;

/**
 * A chunk of answer text. Concatenating every ``delta`` in order yields the
 * full answer.
 */
export const AgentDeltaEventSchema = z.object({
  type: z.literal("delta"),
  text: z.string(),
});

export type AgentDeltaEvent = z.infer<typeof AgentDeltaEventSchema>;

/**
 * Emitted whenever the agent invokes one of the allowed tools. ``ok=false``
 * with a ``rejected`` reason indicates an unsafe/unknown tool the agent
 * tried to call: it is surfaced for transparency but the call did not run.
 */
export const AgentToolCallEventSchema = z.object({
  type: z.literal("tool_call"),
  /** 1-based ordinal of this tool call within the run. */
  ordinal: z.number().int().min(1),
  tool: z.string().min(1),
  /** Whether the call was accepted and executed. */
  ok: z.boolean(),
  /** Present when ``ok=false`` — e.g. "unknown_tool", "budget_exceeded". */
  rejectedReason: z.string().nullable(),
  /** Number of result rows / bytes-ish size, for observability. */
  resultSize: z.number().int().nonnegative(),
});

export type AgentToolCallEvent = z.infer<typeof AgentToolCallEventSchema>;

/**
 * Emitted for each validated citation, as the agent produces it.
 */
export const AgentCiteEventSchema = z.object({
  type: z.literal("cite"),
  citation: AgentCitationSchema,
});

export type AgentCiteEvent = z.infer<typeof AgentCiteEventSchema>;

/**
 * A non-fatal, recoverable problem surfaced mid-stream (e.g. a tool errored
 * but the run continued). Fatal failures abort the SSE stream and are mapped
 * to RFC7807 before the stream begins; once streaming, errors come through
 * here and the run ends with the appropriate stop reason.
 */
export const AgentErrorEventSchema = z.object({
  type: z.literal("error"),
  message: z.string(),
});

export type AgentErrorEvent = z.infer<typeof AgentErrorEventSchema>;

/**
 * Terminal event. Carries the complete answer, every validated citation,
 * the stop reason, and run telemetry.
 */
export const AgentEndEventSchema = z.object({
  type: z.literal("end"),
  answer: z.string(),
  citations: z.array(AgentCitationSchema),
  stopReason: AgentStopReasonEnum,
  toolCalls: z.number().int().nonnegative(),
  elapsedMs: z.number().nonnegative(),
  /** Best-effort token + cost accounting for the whole run. */
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

export type AgentEndEvent = z.infer<typeof AgentEndEventSchema>;

/**
 * Discriminated union of every SSE event the agent can emit.
 */
export const AgentEventSchema = z.discriminatedUnion("type", [
  AgentStartEventSchema,
  AgentDeltaEventSchema,
  AgentToolCallEventSchema,
  AgentCiteEventSchema,
  AgentErrorEventSchema,
  AgentEndEventSchema,
]);

export type AgentEvent = z.infer<typeof AgentEventSchema>;

/**
 * Aggregate (non-streaming) result. Mirrors the data on the ``end`` event;
 * useful for the saved-search runner which consumes the agent in-process and
 * does not need the SSE wire format.
 */
export const AgentSearchResultSchema = z.object({
  runId: z.string().min(1),
  promptVersion: z.string().min(1),
  answer: z.string(),
  citations: z.array(AgentCitationSchema),
  stopReason: AgentStopReasonEnum,
  toolCalls: z.number().int().nonnegative(),
  elapsedMs: z.number().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

export type AgentSearchResult = z.infer<typeof AgentSearchResultSchema>;
